import { RepeatIcon, SettingsIcon } from '@chakra-ui/icons';
import {
  Box,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalFooter,
  Text,
  Button,
  useDisclosure,
  VStack
} from '@chakra-ui/react';
import React, { FC, useState, useEffect } from 'react';

import { useUserState } from '@app/state/userStore';
import { decToFrac } from '@app/helpers/format';
import { Percent } from '@cryptoalgebra/kim-sdk';

interface Props {
  onRefresh?: () => void;
}

function getSlippage() {
  const slippage = localStorage.getItem('SWAP_SLIPPAGE');

  if (slippage && slippage !== 'auto') {
    const frac = decToFrac(+slippage / 100);

    return { storeValue: new Percent(frac[0], frac[1]), value: slippage };
  }

  return {
    storeValue: 'auto' as const,
    value: ''
  };
}

function getDeadline() {
  const deadline = localStorage.getItem('SWAP_DEADLINE');

  if (deadline) {
    return { storeValue: Number(deadline), value: deadline };
  }

  return {
    storeValue: 180,
    value: String(180)
  };
}

export const HeaderControlsV3: FC<Props> = ({ onRefresh }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [deadline, setDeadline] = useState('');
  const [slp, setSlp] = useState('');
  const [mode, setMode] = useState('manual');

  const {
    actions: { setSlippage, setTxDeadline }
  } = useUserState();

  useEffect(() => {
    const d = getDeadline();

    setDeadline(d.value);
    setTxDeadline(d.storeValue);

    const s = getSlippage();

    setSlp(s.value);
    setSlippage(s.storeValue);

    if (s.storeValue === 'auto') {
      setMode('auto');
    }
  }, [setSlippage, setTxDeadline]);

  return (
    <HStack w="100%" justify="flex-end" mb="4px" gap="16px">
      {onRefresh && (
        <IconButton
          aria-label="refresh"
          icon={
            <RepeatIcon
              w="18px"
              h="18px"
              color="neutral.300"
              transition="color 0.2 ease"
              _hover={{ color: 'white', cursor: 'pointer' }}
            />
          }
          onClick={onRefresh}
        />
      )}
      <IconButton
        aria-label="refresh"
        p="12px"
        border="1px solid"
        borderRadius="8px"
        borderColor="whiteOpacity.6"
        icon={
          <SettingsIcon
            w="18px"
            h="18px"
            color="neutral.300"
            transition="color 0.2 ease"
            _hover={{ color: 'white', cursor: 'pointer' }}
          />
        }
        onClick={onOpen}
      />
      <Modal
        isOpen={isOpen}
        onClose={() => {
          onClose();
        }}
        isCentered
        motionPreset="slideInBottom"
        size="xs"
      >
        <ModalOverlay />
        <ModalContent sx={{ maxWidth: ['100vw', '360px'] }}>
          <ModalHeader>
            <HStack justify="space-between" px={0} py={0} mb="16px">
              <Text fontSize="16px" color="white" fontWeight="500">
                Settings
              </Text>
              <ModalCloseButton
                onClick={() => {
                  onClose();
                }}
              />
            </HStack>
          </ModalHeader>
          <VStack w="100%">
            <Box mb="16px" w="100%">
              <Text
                textAlign="left"
                w="100%"
                color="neutral.300"
                fontSize="12px"
                lineHeight="18px"
                letterSpacing="-0.18px"
                mb="4px"
              >
                Slippage (%)
              </Text>
              <HStack>
                <Input
                  variant="filled"
                  borderRadius="8px"
                  bg="neutral.700"
                  fontSize="xl"
                  p="12px"
                  border="none"
                  color="gray.100"
                  gridArea="input"
                  _focus={{
                    outline: 'none'
                  }}
                  _disabled={{
                    opacity: 0.5
                  }}
                  _hover={{ bg: 'neutral.700' }}
                  w="100%"
                  // type="number"
                  step={0.1}
                  value={mode === 'auto' ? 'auto' : slp}
                  disabled={mode === 'auto'}
                  onChange={e => {
                    const val = e.target.value;

                    setSlp(val);
                  }}
                  placeholder="0"
                />
                <HStack
                  w="fit-content"
                  alignItems="flex-start"
                  gap="3px"
                  px="4px"
                  py="4px"
                  sx={{
                    background: 'neutral.1000',
                    border: '1px solid',
                    borderColor: 'neutral.700',
                    borderRadius: '6px'
                  }}
                >
                  <Button
                    variant="tertiary"
                    onClick={() => {
                      setMode('manual');
                    }}
                    sx={{
                      minW: '60px',
                      fontSize: 12,
                      padding: '14px 8px',
                      color: mode === 'manual' ? 'orange' : 'gray.4',
                      bg: mode === 'manual' ? 'gray.2' : 'transparent'
                    }}
                    size="md"
                  >
                    Manual
                  </Button>
                  <Button
                    variant="tertiary"
                    onClick={() => {
                      setMode('auto');
                    }}
                    sx={{
                      minW: '60px',
                      fontSize: 12,
                      padding: '14px 8px',
                      color: mode === 'auto' ? 'orange' : 'gray.4',
                      bg: mode === 'auto' ? 'gray.2' : 'transparent'
                    }}
                    size="md"
                  >
                    Auto
                  </Button>
                </HStack>
              </HStack>
            </Box>
            <Box mb="16px" w="100%">
              <Text
                textAlign="left"
                w="100%"
                color="neutral.300"
                fontSize="12px"
                lineHeight="18px"
                letterSpacing="-0.18px"
                mb="4px"
              >
                Deadline (mins)
              </Text>
              <Input
                variant="filled"
                borderRadius="8px"
                bg="neutral.700"
                fontSize="xl"
                p="12px"
                border="none"
                color="gray.100"
                gridArea="input"
                _focus={{
                  outline: 'none'
                }}
                w="100%"
                _hover={{ bg: 'neutral.700' }}
                type="number"
                value={deadline}
                onChange={e => setDeadline(e.target.value)}
                placeholder="0"
              />
            </Box>
          </VStack>
          <ModalFooter justifyContent="space-between" gap="16px">
            <Button
              w="100%"
              bg="neutral.700"
              py="10px"
              px="20px"
              borderRadius="12px"
              onClick={async () => {
                setSlippage('auto');
                setDeadline(String(180));
              }}
              color="neutral.400"
              _hover={{ background: 'orange', color: 'white' }}
            >
              <Text
                fontSize="16px"
                fontFamily="'Inter', sans-serif"
                fontWeight="500"
                lineHeight="20px"
              >
                Reset to defaults
              </Text>
            </Button>
            <Button
              w="100%"
              bg="neutral.700"
              py="10px"
              px="20px"
              borderRadius="12px"
              onClick={async () => {
                if (mode === 'manual') {
                  const frac = decToFrac(+slp / 100);

                  localStorage.setItem('SWAP_SLIPPAGE', slp);
                  setSlippage(new Percent(frac[0], frac[1]));
                } else if (mode === 'auto') {
                  localStorage.setItem('SWAP_SLIPPAGE', 'auto');
                  setSlippage('auto');
                }

                localStorage.setItem('SWAP_DEADLINE', deadline);
                setTxDeadline(Number(deadline));

                onClose();
              }}
              color="neutral.400"
              _hover={{ background: 'orange', color: 'white' }}
            >
              <Text
                fontSize="16px"
                fontFamily="'Inter', sans-serif"
                fontWeight="500"
                lineHeight="20px"
              >
                Save settings
              </Text>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </HStack>
  );
};
